
(function () {
  const INIT_FLAG = 'variantAutoColorFixInit';
  const ZERO_PRICE_PATTERNS = [
    '€0,00',
    '€ 0,00',
    '0,00 €',
    '$0.00',
    '$ 0.00',
    '0.00',
    '0,00'
  ];

  function getPickers(root = document) {
    return Array.from(root.querySelectorAll('variant-picker'));
  }

  function getProductInfoFromPicker(picker) {
    return picker.closest('product-info, .product__info-wrapper, .product__info');
  }

  function getProductFormFromPicker(picker) {
    const productInfo = getProductInfoFromPicker(picker);
    return productInfo?.querySelector('form.product-form') || null;
  }

  function getVariantIdInput(picker) {
    return getProductFormFromPicker(picker)?.querySelector('[data-product-form-variant]') || null;
  }

  function getPriceElement(picker) {
    return getProductInfoFromPicker(picker)?.querySelector('.product-price__current-price') || null;
  }

  function normalizeText(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function looksLikeZeroPrice(text) {
    const normalized = normalizeText(text);
    if (!normalized) return false;

    if (ZERO_PRICE_PATTERNS.includes(normalized)) return true;

    return /(^|[^\d])0([.,]00)?([^\d]|$)/.test(normalized) && /[€$£]/.test(normalized);
  }

  function getStoredLastValidPrice(priceEl) {
    return priceEl?.dataset.lastValidPrice || '';
  }

  function setStoredLastValidPrice(priceEl, value) {
    if (!priceEl) return;
    const normalized = normalizeText(value);
    if (!normalized) return;
    if (looksLikeZeroPrice(normalized)) return;
    priceEl.dataset.lastValidPrice = normalized;
  }

  function restoreLastValidPrice(priceEl) {
    const lastValid = getStoredLastValidPrice(priceEl);
    if (priceEl && lastValid) {
      priceEl.textContent = lastValid;
    }
  }

  function getColorLabels(picker) {
    return Array.from(
      picker.querySelectorAll('label[data-color-swatches-picker-label][for]')
    );
  }

  function getInputFromLabel(label) {
    const id = label.getAttribute('for');
    return id ? document.getElementById(id) : null;
  }

  function isUnavailable(label, input) {
    if (!label || !input) return true;
    if (input.disabled) return true;

    return (
      label.hasAttribute('no-available') ||
      label.classList.contains('disabled') ||
      label.classList.contains('is-disabled') ||
      label.getAttribute('aria-disabled') === 'true'
    );
  }

  function getCheckedColorInput(picker) {
    for (const label of getColorLabels(picker)) {
      const input = getInputFromLabel(label);
      if (input?.checked) return input;
    }
    return null;
  }

  function getFirstAvailableColorInput(picker) {
    for (const label of getColorLabels(picker)) {
      const input = getInputFromLabel(label);
      if (input && !isUnavailable(label, input)) return input;
    }
    return null;
  }

  function isColorInput(picker, input) {
    if (!(input instanceof HTMLInputElement)) return false;

    return getColorLabels(picker).some(
      (label) => label.getAttribute('for') === input.id
    );
  }

  function triggerSelection(picker, input) {
    if (!input) return;

    input.checked = true;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    const label = picker.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    if (label) label.click();
  }

  function markResolving(picker, state) {
    const productInfo = getProductInfoFromPicker(picker);
    if (!productInfo) return;

    if (state) {
      productInfo.dataset.variantResolving = 'true';
    } else {
      delete productInfo.dataset.variantResolving;
    }
  }

  function isResolving(picker) {
    return getProductInfoFromPicker(picker)?.dataset.variantResolving === 'true';
  }

  function ensureValidVariant(picker) {
    const variantIdInput = getVariantIdInput(picker);
    const priceEl = getPriceElement(picker);

    const currentColorInput = getCheckedColorInput(picker);
    const currentColorLabel = currentColorInput
      ? picker.querySelector(`label[for="${CSS.escape(currentColorInput.id)}"]`)
      : null;

    const hasValidVariantId = !!variantIdInput?.value;
    const currentColorIsValid =
      currentColorInput &&
      currentColorLabel &&
      !isUnavailable(currentColorLabel, currentColorInput);

    if (hasValidVariantId && currentColorIsValid) {
      markResolving(picker, false);

      const currentPriceText = normalizeText(priceEl?.textContent);
      if (priceEl && currentPriceText && !looksLikeZeroPrice(currentPriceText)) {
        setStoredLastValidPrice(priceEl, currentPriceText);
      } else if (priceEl && looksLikeZeroPrice(currentPriceText)) {
        restoreLastValidPrice(priceEl);
      }
      return;
    }

    markResolving(picker, true);

    if (priceEl) {
      restoreLastValidPrice(priceEl);
    }

    const firstAvailableColorInput = getFirstAvailableColorInput(picker);
    if (!firstAvailableColorInput) {
      requestAnimationFrame(() => markResolving(picker, false));
      return;
    }

    if (currentColorInput !== firstAvailableColorInput || !hasValidVariantId) {
      triggerSelection(picker, firstAvailableColorInput);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const freshVariantId = !!getVariantIdInput(picker)?.value;
        if (freshVariantId) {
          markResolving(picker, false);

          const freshPriceEl = getPriceElement(picker);
          const freshPriceText = normalizeText(freshPriceEl?.textContent);
          if (freshPriceEl && freshPriceText && !looksLikeZeroPrice(freshPriceText)) {
            setStoredLastValidPrice(freshPriceEl, freshPriceText);
          } else if (freshPriceEl) {
            restoreLastValidPrice(freshPriceEl);
          }
        }
      });
    });
  }

  function initPriceGuard(picker) {
    const priceEl = getPriceElement(picker);
    if (!priceEl || priceEl.dataset.priceGuardInit === 'true') return;

    priceEl.dataset.priceGuardInit = 'true';

    const initialText = normalizeText(priceEl.textContent);
    if (initialText && !looksLikeZeroPrice(initialText)) {
      setStoredLastValidPrice(priceEl, initialText);
    }

    const observer = new MutationObserver(() => {
      const currentText = normalizeText(priceEl.textContent);

      if (!currentText) return;

      if (looksLikeZeroPrice(currentText)) {
        restoreLastValidPrice(priceEl);
        return;
      }

      if (!isResolving(picker)) {
        setStoredLastValidPrice(priceEl, currentText);
      }
    });

    observer.observe(priceEl, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  function initPicker(picker) {
    if (!picker || picker.dataset[INIT_FLAG] === 'true') return;

    const colorLabels = getColorLabels(picker);
    if (!colorLabels.length) return;

    picker.dataset[INIT_FLAG] = 'true';

    initPriceGuard(picker);

    let rafId = null;

    function scheduleEnsure() {
      if (rafId) cancelAnimationFrame(rafId);

      markResolving(picker, true);

      const priceEl = getPriceElement(picker);
      if (priceEl) {
        const currentText = normalizeText(priceEl.textContent);
        if (currentText && !looksLikeZeroPrice(currentText)) {
          setStoredLastValidPrice(priceEl, currentText);
        } else {
          restoreLastValidPrice(priceEl);
        }
      }

      rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          ensureValidVariant(picker);
        });
      });
    }

    picker.addEventListener('change', function (event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (isColorInput(picker, target)) return;
      scheduleEnsure();
    }, true);

    picker.addEventListener('click', function (event) {
      const label = event.target.closest('label[for]');
      if (!label) return;

      const input = getInputFromLabel(label);
      if (!(input instanceof HTMLInputElement)) return;
      if (isColorInput(picker, input)) return;

      scheduleEnsure();
    }, true);

    scheduleEnsure();
  }

  function initAll(root = document) {
    getPickers(root).forEach(initPicker);
  }

  function boot() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches('variant-picker')) {
          initPicker(node);
        } else {
          node.querySelectorAll?.('variant-picker').forEach(initPicker);
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
